import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppNavigate } from "../lib/navigate";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  Clock,
  Info,
  LoaderCircle,
  MapPin,
  Minus,
  Plus,
  Truck,
  Trash2,
  X,
  Package,
} from "lucide-react";
import api from "../lib/api";
import { savePersistentToken, removeSessionToken } from "../lib/tokenStorage.js";
import AuthDrawer from "../components/AuthDrawer.jsx";
import GuestIdentityDrawer from "../components/GuestIdentityDrawer.jsx";
import ShippingAddressDrawer from "../components/ShippingAddressDrawer.jsx";
import { clearCart, loadCart, saveCart } from "../lib/cartStorage";
import {
  formatCurrency,
  hexToRgba,
  normalizeHexColor,
  resolveProductUnitLabel,
  roundCurrency,
} from "../lib/productUtils";
import productPlaceholder from "../assets/product-placeholder.svg";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_BRANDING = {
  appName: "My PingUp",
  logoUrl: "",
  primaryColor: "#38bdf8",
  accentColor: "#f472b6",
};

const productImageFallback = productPlaceholder;

const DAY_ABBR_TO_INDEX = { DOM: 0, LUN: 1, MAR: 2, MER: 3, GIO: 4, VEN: 5, SAB: 6 };
const INDEX_TO_SHORT = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeCartTotals(cartLines, vatMode = "exclusive") {
  let subtotal = 0;
  let vatAmount = 0;
  let items = 0;
  for (const line of cartLines) {
    const qty = Number(line.quantity) || 0;
    const price = Number(line.unitPrice) || 0;
    const lineSubtotal = price * qty;
    const vatPct = Number(line.productSnapshot?.price?.vat) || 0;
    subtotal += lineSubtotal;
    if (vatMode === "inclusive") {
      // VAT is already in the price — extract it for display only
      vatAmount += lineSubtotal * vatPct / (100 + vatPct);
    } else {
      vatAmount += lineSubtotal * (vatPct / 100);
    }
    items += qty;
  }
  return {
    subtotal: roundCurrency(subtotal),
    vatAmount: roundCurrency(vatAmount),
    total: vatMode === "inclusive" ? roundCurrency(subtotal) : roundCurrency(subtotal + vatAmount),
    items,
  };
}

function getNextPickupSlot(openingHours) {
  if (!Array.isArray(openingHours) || !openingHours.length) return null;
  const now = new Date();
  for (let daysAhead = 0; daysAhead < 8; daysAhead++) {
    const candidate = new Date(now);
    candidate.setDate(candidate.getDate() + daysAhead);
    const dayIndex = candidate.getDay(); // 0=Sun
    const dayKey = Object.keys(DAY_ABBR_TO_INDEX).find(
      (k) => DAY_ABBR_TO_INDEX[k] === dayIndex
    );
    if (!dayKey) continue;
    for (const slot of openingHours) {
      const days = Array.isArray(slot.days) ? slot.days : [];
      if (!days.includes(dayKey)) continue;
      const [h, m] = (slot.open || "").split(":").map(Number);
      const slotOpen = new Date(candidate);
      slotOpen.setHours(h || 0, m || 0, 0, 0);
      if (daysAhead === 0 && slotOpen <= now) continue;
      return slotOpen;
    }
  }
  return null;
}

function formatPickupDate(date) {
  if (!date) return null;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${INDEX_TO_SHORT[date.getDay()]} ${dd}/${mm} dalle ${hh}:${min}`;
}

function groupOpeningHours(openingHours) {
  if (!Array.isArray(openingHours)) return [];
  const groups = new Map();
  for (const slot of openingHours) {
    const days = Array.isArray(slot.days) ? slot.days : [];
    if (!days.length) continue;
    const key = days.join(",");
    if (!groups.has(key)) groups.set(key, { label: days.join(", "), slots: [] });
    groups.get(key).slots.push(`${slot.open || "?"}-${slot.close || "?"}`);
  }
  return Array.from(groups.values()).map((g) => ({
    label: g.label,
    hours: g.slots.join(" / "),
  }));
}

function resolveAlertIcon(iconName) {
  const token = String(iconName || "").toLowerCase();
  if (token === "info") return Info;
  if (token === "truck") return Truck;
  if (token === "clock") return Clock;
  if (token === "mappin") return MapPin;
  return AlertCircle;
}

// ---------------------------------------------------------------------------
// SuccessScreen
// ---------------------------------------------------------------------------

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function SuccessScreen({ order, accentColor, logoUrl, appName, checkoutMode, onGoBack }) {
  const orderNumber = order?.orderNumber || "";
  const total = order?.total ?? null;
  const statusName = order?.statusName || "";
  const whatsappUrl = order?.whatsappRedirectUrl || null;
  const showWhatsApp = checkoutMode !== "pingup" && !!whatsappUrl;
  const accentSoft = hexToRgba(accentColor, 0.12);

  return (
    <div className="min-h-screen bg-[#efefef]">
      <div className="mx-auto min-h-screen w-full max-w-[480px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-5 pb-2">
          {logoUrl ? (
            <img src={logoUrl} alt={appName || "Logo"} className="h-8 w-auto object-contain" />
          ) : (
            <span className="text-base font-bold text-[#0f172a]">{appName}</span>
          )}
          <button
            type="button"
            onClick={onGoBack}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#64748b] shadow-sm"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Main card */}
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
          <div className="w-full rounded-2xl bg-white shadow-sm overflow-hidden">
            {/* Top accent strip */}
            <div className="h-1 w-full" style={{ backgroundColor: accentColor }} />

            <div className="px-6 py-7 text-center">
              {/* Check icon */}
              <div
                className="mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center rounded-full"
                style={{ backgroundColor: accentSoft }}
              >
                <Check className="h-8 w-8" style={{ color: accentColor }} />
              </div>

              <h1 className="text-2xl font-bold text-[#0f172a]">Ordine confermato!</h1>
              {orderNumber && (
                <p className="mt-1 text-sm text-[#64748b]">{orderNumber}</p>
              )}

              <p className="mt-3 text-sm leading-relaxed text-[#475569]">
                Il tuo ordine è stato ricevuto con successo.
              </p>
            </div>

            {/* Summary row */}
            {total !== null && (
              <div className="mx-6 mb-6 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#64748b]">Totale</span>
                  <span className="text-xl font-bold text-[#0f172a]">{formatCurrency(total)}</span>
                </div>
                {statusName && (
                  <div className="mt-2 flex items-center justify-between border-t border-[#e8eff8] pt-2">
                    <span className="text-xs text-[#94a3b8]">Stato</span>
                    <span
                      className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                      style={{ backgroundColor: accentSoft, color: accentColor }}
                    >
                      {statusName}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3 px-6 pb-7">
              {showWhatsApp && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] text-base font-semibold text-white shadow-sm transition-all active:scale-[0.98]"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                  Invia tramite WhatsApp
                </a>
              )}
              <button
                type="button"
                onClick={onGoBack}
                className="flex h-12 w-full items-center justify-center rounded-xl border border-[#e2e8f0] bg-white text-sm font-medium text-[#475569] transition-all active:scale-[0.98]"
              >
                Torna allo shop
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PendingApprovalScreen
// ---------------------------------------------------------------------------

function PendingApprovalScreen({ logoUrl, appName, accentColor, onGoBack }) {
  return (
    <div className="flex min-h-screen flex-col bg-[#f8fafc]">
      <div className="flex justify-end px-4 pt-5">
        <button
          type="button"
          onClick={onGoBack}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#64748b] shadow-sm"
          aria-label="Chiudi"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center px-8 pb-8 text-center">
        {logoUrl && (
          <img src={logoUrl} alt={appName || "Logo"} className="mb-6 h-24 w-auto object-contain" />
        )}
        <h1 className="text-3xl font-bold text-[#0f172a]">Registrazione completata!</h1>
        <p className="mt-8 text-xl font-bold leading-snug" style={{ color: accentColor }}>
          Il tuo account<br />è in attesa di approvazione
        </p>
        <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#64748b]">
          Il tuo ordine è stato inviato. Riceverai una notifica via WhatsApp non appena il tuo
          account sarà approvato dal nostro team.
        </p>
      </div>
      <div className="px-6 pb-12">
        <button
          type="button"
          onClick={onGoBack}
          className="flex h-14 mx-auto w-full max-w-xs items-center justify-center rounded-2xl border border-[#d7e0ea] bg-white text-base font-semibold text-[#0f172a] shadow-sm"
        >
          Chiudi
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CheckoutPage
// ---------------------------------------------------------------------------

export default function CheckoutPage() {
  const { tenantId = "default" } = useParams();
  const navigate = useAppNavigate();
  const [cartLines, setCartLines] = useState(() => loadCart(tenantId));
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [storeInfo, setStoreInfo] = useState(null);
  const [orderNotes, setOrderNotes] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountResult, setDiscountResult] = useState(null); // { valid, message, discountAmount }
  const [pricingPreview, setPricingPreview] = useState(null); // { subtotal, vatAmount, total, discountAmount }
  const [validatingCode, setValidatingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [checkoutConflicts, setCheckoutConflicts] = useState([]); // conflict items blocking checkout
  const [orderResult, setOrderResult] = useState(null); // set on success
  const [productsOpen, setProductsOpen] = useState(false); // accordion
  const [webappMe, setWebappMe] = useState(null); // { guest, customer } from /me
  const [showRegDrawer, setShowRegDrawer] = useState(false);
  const [showGuestDrawer, setShowGuestDrawer] = useState(false);
  const [checkoutBlocks, setCheckoutBlocks] = useState([]); // from config
  const [blockAnswers, setBlockAnswers] = useState({}); // { [blockId]: string | string[] }
  const [blockErrors, setBlockErrors] = useState({}); // { [blockId]: true } — required fields not filled
  const [shippingConfig, setShippingConfig] = useState({ enabled: false, cost: 0, freeAbove: null, description: "" });
  const [pickupEnabled, setPickupEnabled] = useState(true); // default true for backward compat
  const [deliveryType, setDeliveryType] = useState("pickup"); // user choice when both enabled
  const [checkoutMode, setCheckoutMode] = useState("wa_otp_custom"); // from config
  const [vatMode, setVatMode] = useState("exclusive"); // from config: exclusive | inclusive
  // wa_redirect: inline guest form fields (name, surname, phone)
  const [guestForm, setGuestForm] = useState({ name: "", surname: "", phone: "" });
  // shipping address drawer
  const [showShippingDrawer, setShowShippingDrawer] = useState(false);
  // pre-checkout reconcile check (runs before opening any drawer)
  const [preChecking, setPreChecking] = useState(false);

  const [useCredit, setUseCredit] = useState(false);
  const [useWelfareCredit, setUseWelfareCredit] = useState(false);

  // ref for scrolling to the conflict block
  const conflictRef = useRef(null);

  // --- theme ---------------------------------------------------------------
  const primaryColor = useMemo(
    () => normalizeHexColor(branding.primaryColor, DEFAULT_BRANDING.primaryColor),
    [branding.primaryColor]
  );
  const accentColor = useMemo(
    () => normalizeHexColor(branding.accentColor, DEFAULT_BRANDING.accentColor),
    [branding.accentColor]
  );

  // --- load config ---------------------------------------------------------
  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const { data } = await api.get("/webapp/config", { headers: { "x-tenant-id": tenantId } });
        if (!active) return;
        const payload = data?.data || {};
        setBranding({
          appName: payload.branding?.appName || DEFAULT_BRANDING.appName,
          logoUrl: payload.branding?.logoUrl || "",
          primaryColor: normalizeHexColor(payload.branding?.primaryColor, DEFAULT_BRANDING.primaryColor),
          accentColor: normalizeHexColor(payload.branding?.accentColor, DEFAULT_BRANDING.accentColor),
        });
        setStoreInfo(payload.storeInfo || null);
        // Merge tenant checkout blocks + group info blocks
        const tenantBlocks = payload.checkoutConfig?.blocks || [];
        const groupInfoBlocks = (payload.groupBlocks || []).filter((b) => b.type?.startsWith("info_"));
        setCheckoutBlocks([...tenantBlocks, ...groupInfoBlocks]);
        setShippingConfig({
          enabled: payload.shipping?.enabled || false,
          cost: payload.shipping?.cost || 0,
          freeAbove: payload.shipping?.freeAbove ?? null,
          description: payload.shipping?.description || "",
        });
        const pEnabled = payload.pickup?.enabled !== false;
        setPickupEnabled(pEnabled);
        // Auto-select delivery type based on what is enabled
        if (!pEnabled && payload.shipping?.enabled) {
          setDeliveryType("delivery");
        } else {
          setDeliveryType("pickup");
        }
        setCheckoutMode(payload.checkoutMode || "wa_otp_custom");
        setVatMode(payload.vatMode || "exclusive");
      } catch {
        if (!active) return;
      }
    }
    load();
    return () => { active = false; };
  }, [tenantId]);

  // --- load customer state -------------------------------------------------
  useEffect(() => {
    let active = true;
    async function loadMe() {
      try {
        const { data } = await api.get("/webapp/customers/me");
        if (!active) return;
        console.log("[Checkout] webappMe loaded:", data?.data);
        setWebappMe(data?.data || null);
      } catch (err) {
        if (!active) return;
        console.error("[Checkout] Failed to load webappMe:", err.response?.data || err.message);
        setWebappMe({ guest: true, customer: null });
      }
    }
    loadMe();
    return () => { active = false; };
  }, [tenantId]);

  // --- sync cart to localStorage -------------------------------------------
  useEffect(() => {
    saveCart(tenantId, cartLines);
  }, [tenantId, cartLines]);

  // --- derived values -------------------------------------------------------
  const baseTotals = useMemo(() => computeCartTotals(cartLines, vatMode), [cartLines, vatMode]);
  const totals = pricingPreview
    ? {
        subtotal: Number(pricingPreview.subtotal) || 0,
        vatAmount: Number(pricingPreview.vatAmount) || 0,
        total: Number(pricingPreview.total) || 0,
        items: baseTotals.items,
      }
    : baseTotals;
  const appliedDiscountAmount = useMemo(() => {
    return Math.max(0, Number(pricingPreview?.discountAmount) || 0);
  }, [pricingPreview?.discountAmount]);
  const unpricedLinesCount = useMemo(() => {
    return cartLines.reduce((acc, line) => {
      const qty = Number(line?.quantity) || 0;
      const unitPrice = Number(line?.unitPrice) || 0;
      return qty > 0 && unitPrice <= 0 ? acc + 1 : acc;
    }, 0);
  }, [cartLines]);
  const hasUnpricedLines = unpricedLinesCount > 0;
  const hasVariablePricingLines = useMemo(
    () => cartLines.some((line) => Number(line?.variableSelection?.value) > 0),
    [cartLines]
  );

  function buildItemsPayloadFromCart(currentCartLines) {
    return currentCartLines.map((line) => {
      const variantId = String(line.variantId || "").trim();
      const snapshotVariants = Array.isArray(line.productSnapshot?.variants)
        ? line.productSnapshot.variants
        : null;
      const isMongoId = /^[a-f\d]{24}$/i.test(variantId);
      const hasValidVariant =
        variantId &&
        (
          (snapshotVariants && snapshotVariants.some((variant) => String(variant?._id) === variantId)) ||
          (!snapshotVariants && isMongoId)
        );

      return {
        lineId: line.id,
        productId: line.productId,
        ...(hasValidVariant ? { variantId } : {}),
        quantity: line.quantity,
        ...(line?.productSnapshot?.updatedAt
          ? { clientProductUpdatedAt: String(line.productSnapshot.updatedAt) }
          : {}),
        ...(line?.variableSelection && Number(line.variableSelection.value) > 0
          ? {
              variableSelection: {
                value: Number(line.variableSelection.value),
              },
            }
          : {}),
        selectedOptions: Array.isArray(line.optionSummaries)
          ? line.optionSummaries
              .filter((s) => !s._isVariableLabel)
              .map((s) => ({
                label: s.label,
                value: s.value,
                priceAddon: Number(s.priceAddon) || 0,
              }))
          : [],
        ...(line.notes && String(line.notes).trim() ? { notes: String(line.notes).trim() } : {}),
      };
    });
  }

  function formatCheckoutConflicts(conflicts) {
    if (!Array.isArray(conflicts) || !conflicts.length) return "Il carrello contiene elementi non validi.";
    const lines = conflicts.slice(0, 3).map((conflict) => {
      const row = Number(conflict?.index) + 1;
      const suffix = Number.isFinite(row) ? `Riga ${row}` : "Riga";
      return `${suffix}: ${conflict?.message || "elemento non valido"}`;
    });
    const remaining = conflicts.length - lines.length;
    return remaining > 0
      ? `${lines.join(" | ")} | +${remaining} altri problemi`
      : lines.join(" | ");
  }

  function mergeReconciledCartLines(currentLines, reconciledLines) {
    if (!Array.isArray(reconciledLines) || !reconciledLines.length) return currentLines;

    const reconciledByLineId = new Map(
      reconciledLines
        .filter((line) => line?.lineId)
        .map((line) => [String(line.lineId), line])
    );

    return currentLines.map((line) => {
      const reconciled = reconciledByLineId.get(String(line.id));
      if (!reconciled) return line;

      return {
        ...line,
        quantity: Number(reconciled.quantity) || line.quantity,
        unitPrice: Number(reconciled.unitPrice) || 0,
        variantId: reconciled.variantId || "",
        variantLabel: reconciled.variantLabel || "",
        optionSummaries: Array.isArray(reconciled.optionSummaries) ? reconciled.optionSummaries : [],
        variableSelection: reconciled.variableSelection || null,
        productSnapshot: reconciled.productSnapshot || line.productSnapshot,
      };
    });
  }

  useEffect(() => {
    let active = true;

    async function refreshPricingPreview() {
      if (!cartLines.length) {
        setPricingPreview(null);
        return;
      }

      try {
        const payload = {
          items: buildItemsPayloadFromCart(cartLines),
          ...(discountResult?.valid && discountCode.trim()
            ? { discountCode: discountCode.trim() }
            : {}),
        };
        const { data } = await api.post("/webapp/orders/preview", payload);
        if (!active) return;
        setPricingPreview(data?.data || null);
      } catch {
        if (!active) return;
        setPricingPreview(null);
      }
    }

    refreshPricingPreview();
    return () => {
      active = false;
    };
  }, [cartLines, discountCode, discountResult?.valid]);

  const nextPickupSlot = useMemo(
    () => getNextPickupSlot(storeInfo?.openingHours),
    [storeInfo]
  );

  const pickupDateLabel = useMemo(() => formatPickupDate(nextPickupSlot), [nextPickupSlot]);

  const openingHoursDisplay = useMemo(
    () => groupOpeningHours(storeInfo?.openingHours),
    [storeInfo]
  );

  const shippingCost = useMemo(() => {
    if (!shippingConfig.enabled || deliveryType !== "delivery") return 0;
    const freeAbove = shippingConfig.freeAbove;
    const isFree = freeAbove !== null && totals.total >= freeAbove;
    return isFree ? 0 : roundCurrency(shippingConfig.cost || 0);
  }, [shippingConfig, deliveryType, totals.total]);

  const finalTotal = useMemo(() => {
    return Math.max(0, roundCurrency(totals.total + shippingCost));
  }, [totals.total, shippingCost]);

  const creditBalance = webappMe?.customer?.credit?.balance ?? 0;
  const welfareCreditBalance = webappMe?.customer?.welfareCredit?.balance ?? 0;
  const isAuthCheckout = Boolean(webappMe && !webappMe.guest);

  const welfareCreditToApply = useMemo(
    () => (useWelfareCredit && isAuthCheckout ? roundCurrency(Math.min(welfareCreditBalance, finalTotal)) : 0),
    [useWelfareCredit, isAuthCheckout, welfareCreditBalance, finalTotal]
  );
  const creditToApply = useMemo(
    () => (useCredit && isAuthCheckout ? roundCurrency(Math.min(creditBalance, Math.max(0, finalTotal - welfareCreditToApply))) : 0),
    [useCredit, isAuthCheckout, creditBalance, finalTotal, welfareCreditToApply]
  );
  const grandTotal = useMemo(
    () => Math.max(0, roundCurrency(finalTotal - welfareCreditToApply - creditToApply)),
    [finalTotal, welfareCreditToApply, creditToApply]
  );

  // --- cart mutations -------------------------------------------------------
  function changeLineQty(lineId, delta) {
    setCartLines((current) =>
      current
        .map((l) => (l.id === lineId ? { ...l, quantity: Math.max(0, l.quantity + delta) } : l))
        .filter((l) => l.quantity > 0)
    );
  }

  function removeLine(lineId) {
    setCartLines((current) => current.filter((l) => l.id !== lineId));
    // Clear any conflict tied to this line so the block auto-hides if all conflicts resolved
    setCheckoutConflicts((prev) => prev.filter((c) => c.lineId !== lineId));
  }

  // Called from the conflict block to remove a specific conflicting cart line
  function removeConflictingLine(conflict) {
    const targetLineId = conflict.lineId;
    const targetProductId = conflict.productId;
    setCartLines((current) => {
      const next = targetLineId
        ? current.filter((l) => l.id !== targetLineId)
        : current.filter((l) => l.productId !== targetProductId);
      saveCart(tenantId, next);
      return next;
    });
    setCheckoutConflicts((prev) =>
      targetLineId
        ? prev.filter((c) => c.lineId !== targetLineId)
        : prev.filter((c) => c.productId !== targetProductId)
    );
  }

  function humanConflictCode(code) {
    const map = {
      PRODUCT_NOT_FOUND: "Non più disponibile nel catalogo.",
      PRODUCT_SOLD_OUT: "Prodotto esaurito.",
      PRODUCT_MAX_QUANTITY_EXCEEDED: "Quantità richiesta non disponibile.",
      CART_ITEM_STALE: "Il prodotto è stato aggiornato, rimuovilo e aggiungilo di nuovo.",
      VARIANT_NOT_FOUND: "Variante non più disponibile.",
    };
    return map[code] || "Prodotto non disponibile.";
  }

  // Validates required checkout blocks. Returns true if all filled, false + sets errors otherwise.
  function validateRequiredBlocks() {
    const errors = {};
    let firstErrorId = null;
    for (const block of checkoutBlocks) {
      if (!block.required || !block.type.startsWith("field_")) continue;
      const answer = blockAnswers[block._id];
      const isEmpty = Array.isArray(answer) ? answer.length === 0 : !String(answer || "").trim();
      if (isEmpty) {
        errors[block._id] = true;
        if (!firstErrorId) firstErrorId = block._id;
      }
    }
    setBlockErrors(errors);
    if (firstErrorId) {
      setTimeout(() => {
        document.getElementById(`block-${firstErrorId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
      return false;
    }
    return true;
  }

  // Pre-checkout reconcile: validates cart before opening any drawer.
  // Returns true if canCheckout, false if conflicts found (sets them + scrolls).
  async function preReconcile() {
    if (!cartLines.length) return true;
    setPreChecking(true);
    setSubmitError("");
    try {
      const itemsPayload = buildItemsPayloadFromCart(cartLines);
      const reconcilePayload = {
        items: itemsPayload,
        ...(discountResult?.valid && discountCode.trim()
          ? { discountCode: discountCode.trim() }
          : {}),
      };
      const reconcileResponse = await api.post("/webapp/orders/reconcile", reconcilePayload);
      const reconcileData = reconcileResponse?.data?.data || {};

      if (Array.isArray(reconcileData?.reconciledCartLines) && reconcileData.reconciledCartLines.length) {
        setCartLines((current) => mergeReconciledCartLines(current, reconcileData.reconciledCartLines));
      }

      if (!reconcileData?.canCheckout) {
        const conflicts = Array.isArray(reconcileData?.conflicts) ? reconcileData.conflicts : [];
        setCheckoutConflicts(conflicts);
        setTimeout(() => {
          conflictRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 80);
        return false;
      }

      setCheckoutConflicts([]);
      if (reconcileData?.preview) {
        setPricingPreview(reconcileData.preview);
      }
      return true;
    } catch {
      setSubmitError("Errore durante la verifica del carrello. Riprova.");
      return false;
    } finally {
      setPreChecking(false);
    }
  }

  // --- discount code -------------------------------------------------------
  async function handleValidateCode() {
    const code = discountCode.trim();
    if (!code || validatingCode) return;
    setValidatingCode(true);
    setDiscountResult(null);
    try {
      const { data } = await api.post("/webapp/discount-codes/validate", {
        code,
        cartTotal: totals.total,
      });
      const result = data?.data || {};
      setDiscountResult({
        valid: result.valid !== false,
        message: result.valid !== false ? "Codice applicato!" : (result.message || "Codice non valido."),
        discountAmount: Number(result.discountAmount) || null,
      });
    } catch (err) {
      setDiscountResult({
        valid: false,
        message: err?.response?.data?.error?.message || "Codice non valido o scaduto.",
        discountAmount: null,
      });
    } finally {
      setValidatingCode(false);
    }
  }

  // --- submit order ---------------------------------------------------------
  // guestOverride: { name, surname, phone } — used by GuestIdentityDrawer for wa_redirect
  async function submitOrder(deliveryAddress = null, guestOverride = null) {
    if (!cartLines.length || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const itemsPayload = buildItemsPayloadFromCart(cartLines);
      const reconcilePayload = {
        items: itemsPayload,
        ...(discountResult?.valid && discountCode.trim()
          ? { discountCode: discountCode.trim() }
          : {}),
      };

      const reconcileResponse = await api.post("/webapp/orders/reconcile", reconcilePayload);
      const reconcileData = reconcileResponse?.data?.data || {};

      if (Array.isArray(reconcileData?.reconciledCartLines) && reconcileData.reconciledCartLines.length) {
        setCartLines((current) => mergeReconciledCartLines(current, reconcileData.reconciledCartLines));
      }

      if (!reconcileData?.canCheckout) {
        // Keep current totals (don't update preview) and show per-item conflict UI
        const conflicts = Array.isArray(reconcileData?.conflicts) ? reconcileData.conflicts : [];
        setCheckoutConflicts(conflicts);
        setShowGuestDrawer(false);
        setShowShippingDrawer(false);
        setSubmitting(false);
        return;
      }

      // All items valid → update preview and clear any previous conflicts
      setCheckoutConflicts([]);
      if (reconcileData?.preview) {
        setPricingPreview(reconcileData.preview);
      }

      const normalizedItemsPayload = Array.isArray(reconcileData?.itemsForCheckout) && reconcileData.itemsForCheckout.length
        ? reconcileData.itemsForCheckout.map(({ lineId, ...item }) => item)
        : itemsPayload.map(({ lineId, ...item }) => item);

      const payload = {
        items: normalizedItemsPayload,
        ...(orderNotes.trim() ? { notes: orderNotes.trim() } : {}),
        ...(discountResult?.valid && discountCode.trim()
          ? { discountCode: discountCode.trim() }
          : {}),
        checkoutAnswers: checkoutBlocks
          .filter((b) => b.type.startsWith("field_") && blockAnswers[b._id] !== undefined && blockAnswers[b._id] !== "" && !(Array.isArray(blockAnswers[b._id]) && blockAnswers[b._id].length === 0))
          .map((b) => ({ blockId: b._id, label: b.label, value: blockAnswers[b._id] })),
        deliveryType: deliveryType,
        ...(deliveryAddress ? { deliveryAddress } : {}),
        ...(useCredit ? { useCredit: true } : {}),
        ...(useWelfareCredit ? { useWelfareCredit: true } : {}),
        // wa_redirect: pass guest identity (from drawer or fallback to state)
        ...(checkoutMode === "wa_redirect"
          ? { guestData: guestOverride ?? { name: guestForm.name.trim(), surname: guestForm.surname.trim(), phone: guestForm.phone.trim() } }
          : {}),
      };
      const { data } = await api.post("/webapp/orders", payload);
      clearCart(tenantId);
      setShowShippingDrawer(false);
      setShowGuestDrawer(false);
      const orderData = data?.data || {};
      setOrderResult(orderData);
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      const msg = err?.response?.data?.error?.message;
      const conflicts = err?.response?.data?.error?.details?.conflicts;
      if (code === "REGISTRATION_REQUIRED" || code === "AUTH_REQUIRED") {
        setShowShippingDrawer(false);
        setShowRegDrawer(true);
      } else if (code === "CHECKOUT_CONFLICT" && Array.isArray(conflicts) && conflicts.length) {
        setSubmitError(formatCheckoutConflicts(conflicts));
      } else {
        setSubmitError(msg || "Errore durante l'invio dell'ordine. Riprova.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Called after successful OTP / magic-link authentication
  async function handleAuthenticated(customer, accessToken) {
    setShowRegDrawer(false);
    if (accessToken) {
      savePersistentToken(tenantId, accessToken);
      removeSessionToken();
    }
    setWebappMe({ guest: false, customer });
    // If delivery mode, show the address drawer instead of submitting directly
    if (deliveryType === "delivery" && shippingConfig.enabled) {
      setShowShippingDrawer(true);
    } else {
      await submitOrder();
    }
  }

  // Called when GuestIdentityDrawer confirms identity (wa_redirect flow)
  async function handleGuestConfirm(guestData) {
    if (deliveryType === "delivery" && shippingConfig.enabled) {
      // Store identity in state so submitOrder can read it when shipping drawer confirms
      setGuestForm(guestData);
      setShowGuestDrawer(false);
      setShowShippingDrawer(true);
    } else {
      await submitOrder(null, guestData);
    }
  }

  async function handleConfirmOrder() {
    if (!cartLines.length || submitting || preChecking) return;

    // Validate required custom blocks first
    if (!validateRequiredBlocks()) return;

    // Always run reconcile first — before opening any drawer
    const canProceed = await preReconcile();
    if (!canProceed) return;

    if (checkoutMode === "wa_redirect") {
      setShowGuestDrawer(true);
      return;
    }
    if (!webappMe || webappMe.guest) {
      setShowRegDrawer(true);
      return;
    }
    // Authenticated: if delivery → show address drawer, else submit directly
    if (deliveryType === "delivery" && shippingConfig.enabled) {
      setShowShippingDrawer(true);
    } else {
      await submitOrder();
    }
  }

  // --- success / pending screen ---------------------------------------------
  if (orderResult) {
    if (orderResult.pendingCustomerApproval) {
      return (
        <PendingApprovalScreen
          logoUrl={branding.logoUrl}
          appName={branding.appName}
          accentColor={accentColor}
          onGoBack={() => navigate(`/${tenantId}`)}
        />
      );
    }
    return (
      <SuccessScreen
        order={orderResult}
        accentColor={accentColor}
        logoUrl={branding.logoUrl}
        appName={branding.appName}
        checkoutMode={checkoutMode}
        onGoBack={() => navigate(`/${tenantId}`)}
      />
    );
  }

  // --- style helpers --------------------------------------------------------
  const tenantTheme = { "--tenant-primary": primaryColor, "--tenant-accent": accentColor };
  const accentStyle = { color: accentColor };
  const primaryStyle = { color: primaryColor };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#efefef]" style={tenantTheme}>
      <div className="mx-auto min-h-screen w-full max-w-[800px] bg-white shadow-sm">

        {/* Sticky header — back arrow + info */}
        <div className="sticky top-0 z-30 border-b border-[#e2e8f0] bg-white">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d7e0ea] bg-white text-[#0f172a]"
              aria-label="Torna indietro"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d7e0ea] bg-white text-[#64748b]"
              aria-label="Informazioni"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="px-4 pb-32 pt-5 space-y-5">

          {/* Title + subtitle */}
          <div>
            <h1 className="text-2xl font-bold text-[#0f172a]">Il tuo ordine</h1>
            <p className="mt-1.5 text-sm leading-relaxed text-[#64748b]">
              Controlla i prodotti inseriti, le quantità e le tempistiche di consegna prima di proseguire.
            </p>
          </div>

          {/* Empty state */}
          {cartLines.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-sm text-[#64748b]">Il carrello è vuoto.</p>
              <button
                type="button"
                onClick={() => navigate(`/${tenantId}`)}
                className="mt-4 text-sm font-semibold"
                style={primaryStyle}
              >
                Torna al catalogo
              </button>
            </div>
          )}

          {cartLines.length > 0 && (
            <>

              {/* Products accordion */}
              <div className="overflow-hidden rounded-2xl border border-[#e2e8f0]">
                {/* Accordion header — always visible */}
                <button
                  type="button"
                  onClick={() => setProductsOpen((v) => !v)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left"
                >
                  <div>
                    <p className="text-sm font-semibold text-[#0f172a]">
                      {baseTotals.items} prodott{baseTotals.items === 1 ? "o" : "i"} nel carrello
                    </p>
                    <p className="text-xs text-[#64748b]">Tocca per verificare o modificare</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-sm font-bold text-[#0f172a]">{productsOpen ? "Nascondi" : "Mostra"}</span>
                    <ChevronDown
                      className="h-4 w-4 text-[#94a3b8] transition-transform duration-200"
                      style={{ transform: productsOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                    />
                  </div>
                </button>

                {/* Accordion body */}
                {productsOpen && (
                  <div className="border-t border-[#eef2f7]">
                    {cartLines.map((line) => {
                      const summary = [
                        line.variantLabel,
                        ...(Array.isArray(line.optionSummaries)
                          ? line.optionSummaries.map((e) => `${e.label}: ${e.value}`)
                          : []),
                      ]
                        .filter(Boolean)
                        .join(" \u2022 ") || resolveProductUnitLabel(line.productSnapshot);

                      return (
                        <div key={line.id} className="border-b border-[#eef2f7] px-3 py-3 last:border-b-0">
                          <div className="flex items-start gap-3">
                            {line.imageUrl ? (
                              <img
                                src={line.imageUrl || productImageFallback}
                                alt={line.productName}
                                className="h-12 w-12 shrink-0 rounded-lg object-cover"
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = productImageFallback; }}
                              />
                            ) : (
                              <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-200 text-slate-500">
                                  <Package className="h-4 w-4" />
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold leading-tight text-[#0f172a]">
                                {line.productName}
                              </p>
                              <p className="mt-0.5 text-xs text-[#64748b]">
                                {summary
                                  ? (Number(line.unitPrice) > 0
                                      ? `${summary} | ${formatCurrency(line.unitPrice)}`
                                      : summary)
                                  : (Number(line.unitPrice) > 0 ? formatCurrency(line.unitPrice) : "")}
                              </p>
                              {line.notes ? (
                                <p className="mt-1 text-xs italic text-[#64748b]">Nota: {line.notes}</p>
                              ) : null}
                            </div>
                            <div className="inline-flex h-8 items-center rounded-lg border border-[#dbe5ef]">
                              <button
                                type="button"
                                onClick={() => changeLineQty(line.id, -1)}
                                className="inline-flex h-8 w-8 items-center justify-center text-[#334155]"
                                aria-label="Riduci quantità"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="min-w-[24px] text-center text-sm font-semibold text-[#0f172a]">
                                {line.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() => changeLineQty(line.id, 1)}
                                className="inline-flex h-8 w-8 items-center justify-center text-[#334155]"
                                aria-label="Aumenta quantità"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Checkout blocks */}
              {checkoutBlocks.map((block) => {
                const answer = blockAnswers[block._id];
                const hasError = !!blockErrors[block._id];
                const setAnswer = (v) => {
                  setBlockAnswers((prev) => ({ ...prev, [block._id]: v }));
                  if (blockErrors[block._id]) setBlockErrors((prev) => { const n = { ...prev }; delete n[block._id]; return n; });
                };

                if (block.type === "info_text") {
                  return (
                    <div key={block._id} className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
                      {block.label ? <p className="mb-1 text-xs font-semibold text-[#475569]">{block.label}</p> : null}
                      <p className="text-sm text-[#334155]">{block.content || block.description || block.label}</p>
                    </div>
                  );
                }

                if (block.type === "info_alert") {
                  const AlertIcon = block.icon ? resolveAlertIcon(block.icon) : null;
                  const isDestructive = block.variant === "destructive";
                  return (
                    <div
                      key={block._id}
                      className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 ${
                        isDestructive
                          ? "border-red-200 bg-red-50"
                          : "border-amber-200 bg-amber-50"
                      }`}
                    >
                      {AlertIcon ? (
                        <AlertIcon className={`mt-0.5 h-4 w-4 shrink-0 ${isDestructive ? "text-red-500" : "text-amber-500"}`} />
                      ) : null}
                      <div className="space-y-0.5">
                        <p className={`text-sm font-semibold ${isDestructive ? "text-red-800" : "text-amber-800"}`}>
                          {block.title || block.label || "Avviso"}
                        </p>
                        {(block.description || block.content) ? (
                          <p className={`text-sm ${isDestructive ? "text-red-700" : "text-amber-700"}`}>{block.description || block.content}</p>
                        ) : null}
                      </div>
                    </div>
                  );
                }

                if (block.type === "info_store") {
                  if (!storeInfo) {
                    return (
                      <div key={block._id} className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 text-sm text-[#64748b]">
                        Informazioni negozio non disponibili.
                      </div>
                    );
                  }

                  return (
                    <div key={block._id} className="overflow-hidden rounded-2xl border border-[#e2e8f0]">
                      <div className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest" style={primaryStyle}>
                              {block.label || "RITIRO IN NEGOZIO:"}
                            </p>
                            {pickupDateLabel && (
                              <p className="mt-0.5 text-sm font-bold text-[#0f172a]">
                                {pickupDateLabel}
                              </p>
                            )}
                          </div>
                          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#94a3b8]" />
                        </div>

                        {(storeInfo.address || storeInfo.city) && (
                          <div className="mt-2 flex items-start gap-1.5">
                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#94a3b8]" />
                            <p className="text-sm text-[#475569]">
                              {[storeInfo.address, storeInfo.city].filter(Boolean).join(", ")}
                            </p>
                          </div>
                        )}

                        {storeInfo.phone && (
                          <div className="mt-1.5 flex items-start gap-1.5">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#94a3b8]" />
                            <p className="text-sm text-[#475569]">{storeInfo.phone}</p>
                          </div>
                        )}

                        {openingHoursDisplay.length > 0 && (
                          <div className="mt-2.5 space-y-0.5">
                            <p className="text-xs font-medium text-[#475569]">
                              Orari negozio:
                            </p>
                            {openingHoursDisplay.map((row, idx) => (
                              <p key={idx} className="text-xs text-[#64748b]">
                                <span className="font-semibold">{row.label}</span>
                                <span className="mx-1 text-[#94a3b8]">{">"}  </span>
                                <span>{row.hours}</span>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={block._id} id={`block-${block._id}`} className="space-y-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-widest" style={primaryStyle}>
                      {block.label}{block.required ? " *" : ""}
                    </p>
                    {block.type === "field_text" && (
                      <input
                        type="text"
                        value={answer || ""}
                        onChange={(e) => setAnswer(e.target.value)}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none ${
                          hasError ? "border-red-400 bg-red-50 focus:border-red-400" : "border-[#e2e8f0] bg-[#f8fafc] focus:border-[#94a3b8]"
                        }`}
                      />
                    )}
                    {block.type === "field_textarea" && (
                      <textarea
                        value={answer || ""}
                        onChange={(e) => setAnswer(e.target.value)}
                        rows={3}
                        className={`w-full resize-none rounded-xl border px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none ${
                          hasError ? "border-red-400 bg-red-50 focus:border-red-400" : "border-[#e2e8f0] bg-[#f8fafc] focus:border-[#94a3b8]"
                        }`}
                      />
                    )}
                    {block.type === "field_date" && (
                      <input
                        type="date"
                        value={answer || ""}
                        onChange={(e) => setAnswer(e.target.value)}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm text-[#0f172a] focus:outline-none ${
                          hasError ? "border-red-400 bg-red-50 focus:border-red-400" : "border-[#e2e8f0] bg-[#f8fafc] focus:border-[#94a3b8]"
                        }`}
                      />
                    )}
                    {block.type === "field_select" && (
                      <select
                        value={answer || ""}
                        onChange={(e) => setAnswer(e.target.value)}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm text-[#0f172a] focus:outline-none ${
                          hasError ? "border-red-400 bg-red-50 focus:border-red-400" : "border-[#e2e8f0] bg-[#f8fafc] focus:border-[#94a3b8]"
                        }`}
                      >
                        <option value="">Seleziona...</option>
                        {block.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    )}
                    {block.type === "field_multiselect" && (
                      <div className={`flex flex-wrap gap-2 rounded-xl p-1 ${
                        hasError ? "outline outline-1 outline-red-400 bg-red-50" : ""
                      }`}>
                        {block.options.map((opt) => {
                          const selected = Array.isArray(answer) && answer.includes(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                const current = Array.isArray(answer) ? answer : [];
                                setAnswer(selected ? current.filter((v) => v !== opt) : [...current, opt]);
                              }}
                              className="rounded-full border px-3 py-1 text-xs font-semibold transition-colors"
                              style={selected ? { backgroundColor: primaryColor, borderColor: primaryColor, color: "#fff" } : {}}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {hasError && (
                      <p className="text-xs font-medium text-red-500">Campo obbligatorio.</p>
                    )}
                  </div>
                );
              })}
              {/* Note */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold tracking-widest uppercase" style={primaryStyle}>
                  NOTE
                </p>
                <p className="text-xs text-[#64748b]">
                  Scrivi qui ulteriori indicazioni per il tuo ordine:
                </p>
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Scrivi note..."
                  rows={3}
                  className="w-full resize-none rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#94a3b8] focus:outline-none"
                />
              </div>

              {/* Discount code */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold tracking-widest uppercase" style={primaryStyle}>
                  CODICE SCONTO
                </p>
                <p className="text-xs text-[#64748b]">
                  Hai ricevuto un codice sconto? Inseriscilo qui:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value.toUpperCase());
                      setDiscountResult(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleValidateCode();
                    }}
                    placeholder="Inserisci codice..."
                    className="min-w-0 flex-1 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#94a3b8] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleValidateCode}
                    disabled={!discountCode.trim() || validatingCode}
                    className="shrink-0 rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-40"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {validatingCode ? "..." : "Applica"}
                  </button>
                </div>
                {discountResult && (
                  <div
                    className={`flex items-center gap-1.5 text-xs font-medium ${
                      discountResult.valid ? "text-green-600" : "text-[#ef4444]"
                    }`}
                  >
                    {discountResult.valid ? (
                      <Check className="h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    )}
                    <span>{discountResult.message}</span>
                  </div>
                )}
              </div>

              {/* Delivery type section */}
              {(pickupEnabled && shippingConfig.enabled) ? (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={primaryStyle}>
                    TIPO DI CONSEGNA
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ value: "pickup", label: "Ritiro in negozio" }, { value: "delivery", label: "Spedizione" }].map((opt) => {
                      const active = deliveryType === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setDeliveryType(opt.value)}
                          className="rounded-xl border px-4 py-3 text-sm font-semibold transition-colors"
                          style={active ? { backgroundColor: primaryColor, borderColor: primaryColor, color: "#fff" } : { borderColor: "#e2e8f0", color: "#475569" }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {deliveryType === "delivery" && (
                    <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 text-sm">
                      <p className="font-semibold text-[#0f172a]">
                        Costo spedizione:{" "}
                        {shippingCost === 0 ? (
                          <span className="text-emerald-600">GRATIS</span>
                        ) : (
                          formatCurrency(shippingCost)
                        )}
                      </p>
                      {shippingConfig.freeAbove !== null && shippingCost > 0 && (
                        <p className="mt-0.5 text-xs text-[#64748b]">
                          Gratuita per ordini sopra {formatCurrency(shippingConfig.freeAbove)}
                        </p>
                      )}
                      {shippingConfig.description && (
                        <p className="mt-0.5 text-xs text-[#64748b]">{shippingConfig.description}</p>
                      )}
                    </div>
                  )}
                  {deliveryType === "pickup" && storeInfo && (
                    <div className="overflow-hidden rounded-xl border border-[#e2e8f0]">
                      <div className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest" style={primaryStyle}>
                              RITIRO IN NEGOZIO:
                            </p>
                            {pickupDateLabel && (
                              <p className="mt-0.5 text-sm font-bold text-[#0f172a]">
                                {pickupDateLabel}
                              </p>
                            )}
                          </div>
                          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#94a3b8]" />
                        </div>

                        {(storeInfo.address || storeInfo.city) && (
                          <div className="mt-2 flex items-start gap-1.5">
                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#94a3b8]" />
                            <p className="text-sm text-[#475569]">
                              {[storeInfo.address, storeInfo.city].filter(Boolean).join(", ")}
                            </p>
                          </div>
                        )}

                        {openingHoursDisplay.length > 0 && (
                          <div className="mt-2.5 space-y-0.5">
                            <p className="text-xs font-medium text-[#475569]">
                              Passa in negozio a ritirare il tuo ordine:
                            </p>
                            {openingHoursDisplay.map((row, idx) => (
                              <p key={idx} className="text-xs text-[#64748b]">
                                <span className="font-semibold">{row.label}</span>
                                <span className="mx-1 text-[#94a3b8]">{">"}  </span>
                                <span>{row.hours}</span>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : !pickupEnabled && shippingConfig.enabled ? (
                /* Only delivery enabled — show cost info without picker */
                <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 text-sm">
                  <p className="font-semibold text-[#0f172a]">
                    Spedizione:{" "}
                    {shippingCost === 0 ? (
                      <span className="text-emerald-600">GRATIS</span>
                    ) : (
                      formatCurrency(shippingCost)
                    )}
                  </p>
                  {shippingConfig.freeAbove !== null && shippingCost > 0 && (
                    <p className="mt-0.5 text-xs text-[#64748b]">
                      Gratuita per ordini sopra {formatCurrency(shippingConfig.freeAbove)}
                    </p>
                  )}
                  {shippingConfig.description && (
                    <p className="mt-0.5 text-xs text-[#64748b]">{shippingConfig.description}</p>
                  )}
                </div>
              ) : null}

              {/* Price summary */}
              <div className="overflow-hidden rounded-2xl border border-[#e2e8f0]">
                <div className="space-y-1.5 px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#64748b]">Totale parziale:</span>
                    <span className="font-medium text-[#475569]">
                      {formatCurrency(totals.subtotal)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#64748b]">{vatMode === "inclusive" ? "di cui IVA:" : "+ IVA:"}</span>
                    <span className="font-medium text-[#475569]">
                      {formatCurrency(totals.vatAmount)}
                    </span>
                  </div>
                  {appliedDiscountAmount > 0 ? (
                    <div className="flex items-center justify-between text-sm text-green-600">
                      <span>Sconti applicati:</span>
                      <span className="font-medium">
                        − {formatCurrency(appliedDiscountAmount)}
                      </span>
                    </div>
                  ) : null}
                  {shippingCost > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#64748b]">Spedizione:</span>
                      <span className="font-medium text-[#475569]">{formatCurrency(shippingCost)}</span>
                    </div>
                  )}
                  {/* Credit toggles — only shown to authenticated customers with credit */}
                  {isAuthCheckout && welfareCreditBalance > 0 && (
                    <button
                      type="button"
                      onClick={() => setUseWelfareCredit((v) => !v)}
                      className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
                        useWelfareCredit
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : "border-[#e2e8f0] bg-[#f8fafc] text-[#64748b]"
                      }`}
                    >
                      <span className="font-medium">Usa credito welfare (€{welfareCreditBalance.toFixed(2)} disp.)</span>
                      <span className={`text-sm font-semibold ${useWelfareCredit ? "text-emerald-700" : "text-[#94a3b8]"}`}>
                        {useWelfareCredit ? `− ${formatCurrency(welfareCreditToApply)}` : "Applica"}
                      </span>
                    </button>
                  )}
                  {isAuthCheckout && creditBalance > 0 && (
                    <button
                      type="button"
                      onClick={() => setUseCredit((v) => !v)}
                      className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
                        useCredit
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : "border-[#e2e8f0] bg-[#f8fafc] text-[#64748b]"
                      }`}
                    >
                      <span className="font-medium">Usa credito negozio (€{creditBalance.toFixed(2)} disp.)</span>
                      <span className={`text-sm font-semibold ${useCredit ? "text-emerald-700" : "text-[#94a3b8]"}`}>
                        {useCredit ? `− ${formatCurrency(creditToApply)}` : "Applica"}
                      </span>
                    </button>
                  )}
                  <div className="flex items-baseline justify-between border-t border-[#e2e8f0] pt-2">
                    <span className="text-sm font-bold uppercase tracking-wide" style={primaryStyle}>
                      TOTALE ORDINE:
                    </span>
                    <span className="text-2xl font-bold text-[#0f172a]">
                      {formatCurrency(grandTotal)}
                    </span>
                  </div>
                </div>
                <div className="border-t border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5">
                  {hasVariablePricingLines ? (
                    <p className="text-[11px] leading-relaxed text-[#94a3b8]">
                      NB. il prezzo totale può essere soggetto a variazioni in base ai prodotti a prezzo variabile selezionati.
                    </p>
                  ) : null}
                  {hasUnpricedLines ? (
                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      <p className="text-[11px] leading-relaxed text-amber-800">
                        Totale parziale: {unpricedLinesCount} prodott{unpricedLinesCount === 1 ? "o" : "i"} senza prezzo.
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Pickup / store info box — shown when only pickup is available (no delivery) */}
              {storeInfo && pickupEnabled && !shippingConfig.enabled && (
                <div className="overflow-hidden rounded-2xl border border-[#e2e8f0]">
                  <div className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest" style={primaryStyle}>
                          RITIRO IN NEGOZIO:
                        </p>
                        {pickupDateLabel && (
                          <p className="mt-0.5 text-sm font-bold text-[#0f172a]">
                            {pickupDateLabel}
                          </p>
                        )}
                      </div>
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#94a3b8]" />
                    </div>

                    {(storeInfo.address || storeInfo.city) && (
                      <div className="mt-2 flex items-start gap-1.5">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#94a3b8]" />
                        <p className="text-sm text-[#475569]">
                          {[storeInfo.address, storeInfo.city].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    )}

                    {openingHoursDisplay.length > 0 && (
                      <div className="mt-2.5 space-y-0.5">
                        <p className="text-xs font-medium text-[#475569]">
                          Passa in negozio a ritirare il tuo ordine:
                        </p>
                        {openingHoursDisplay.map((row, idx) => (
                          <p key={idx} className="text-xs text-[#64748b]">
                            <span className="font-semibold">{row.label}</span>
                            <span className="mx-1 text-[#94a3b8]">{">"}  </span>
                            <span>{row.hours}</span>
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}




              {/* Checkout conflicts: one or more products blocked */}
              {checkoutConflicts.length > 0 && (
                <div ref={conflictRef} className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <p className="text-sm font-semibold text-amber-800">
                      {checkoutConflicts.length === 1
                        ? "Un prodotto non è più disponibile"
                        : `${checkoutConflicts.length} prodotti non sono più disponibili`}
                    </p>
                  </div>
                  <div className="space-y-2.5">
                    {checkoutConflicts.map((conflict) => {
                      const cartLine = cartLines.find(
                        (l) =>
                          (conflict.lineId && l.id === conflict.lineId) ||
                          l.productId === conflict.productId
                      );
                      const productName =
                        cartLine?.productSnapshot?.name ||
                        cartLine?.name ||
                        `Prodotto ${Number(conflict.index) + 1}`;
                      return (
                        <div
                          key={conflict.lineId || conflict.productId || conflict.index}
                          className="flex items-center justify-between gap-3 rounded-xl bg-white/70 px-3 py-2.5"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-amber-900">{productName}</p>
                            <p className="text-xs text-amber-700">{humanConflictCode(conflict.code)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeConflictingLine(conflict)}
                            className="shrink-0 rounded-xl bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-200 active:bg-amber-300"
                          >
                            Rimuovi
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Submit error */}
              {submitError && (
                <div className="flex items-start gap-2 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5 text-sm text-[#ef4444]">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sticky confirm button */}
        {cartLines.length > 0 && (
          <div className="pointer-events-none fixed bottom-0 left-1/2 z-30 w-full max-w-[800px] -translate-x-1/2">
            <div className="pointer-events-auto border border-b-0 border-[#e2e8f0] bg-white px-4 pb-5 pt-3 shadow-[0_-3px_12px_rgba(0,0,0,0.08)]">
              <button
                type="button"
                onClick={handleConfirmOrder}
                disabled={submitting || preChecking}
                className="flex h-14 w-full items-center justify-between rounded-xl px-5 text-white disabled:opacity-60"
                style={{ backgroundColor: primaryColor }}
              >
                <span className="text-sm font-bold uppercase tracking-wide">
                  {submitting ? "Invio in corso..." : preChecking ? "Verifica in corso..." : "CONFERMA ORDINE"}
                </span>
                <span className="text-lg font-bold">{formatCurrency(finalTotal)}</span>
              </button>
            </div>
          </div>
        )}

        {/* Auth drawer for wa_otp / wa_otp_custom modes */}
        <AuthDrawer
          open={showRegDrawer && checkoutMode !== "wa_redirect"}
          onClose={() => setShowRegDrawer(false)}
          primaryColor={primaryColor}
          tenantId={tenantId}
          requireAddress={false}
          context="checkout"
          onAuthenticated={handleAuthenticated}
        />

        {/* Shipping address drawer — final step before submitting delivery orders */}
        <ShippingAddressDrawer
          open={showShippingDrawer}
          onClose={() => setShowShippingDrawer(false)}
          primaryColor={primaryColor}
          savedAddresses={webappMe?.customer?.addresses || []}
          submitting={submitting}
          submitError={submitError}
          onConfirm={(address) => submitOrder(address)}
        />

        {/* wa_redirect: collect guest identity before submitting */}
        <GuestIdentityDrawer
          open={showGuestDrawer}
          onClose={() => setShowGuestDrawer(false)}
          primaryColor={primaryColor}
          submitting={submitting}
          submitError={submitError}
          onConfirm={handleGuestConfirm}
        />
      </div>
    </div>
  );
}
